import { useEffect, useRef, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useTheme } from '../context/ThemeContext';

// Particle system for AI effect
function Particle({ x, y, size, duration, delay, theme }) {
  return (
    <motion.div
      className="absolute rounded-full"
      style={{
        left: `${x}%`,
        top: `${y}%`,
        width: size,
        height: size,
        backgroundColor: theme === 'dark' 
          ? `rgba(99, 179, 237, ${Math.random() * 0.4 + 0.1})`
          : `rgba(20, 184, 166, ${Math.random() * 0.3 + 0.05})`
      }}
      animate={{
        y: [0, -40, 0],
        x: [0, Math.random() * 30 - 15, 0],
        opacity: [0.1, 0.7, 0.1],
        scale: [1, 1.5, 1]
      }}
      transition={{
        duration,
        delay,
        repeat: Infinity,
        ease: "easeInOut"
      }}
    />
  );
}

// Floating particles with more density
export function FloatingParticles({ count = 60 }) {
  const { theme } = useTheme();
  
  const particles = useMemo(() => 
    Array.from({ length: count }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 6 + 2,
      duration: Math.random() * 20 + 15,
      delay: Math.random() * 5
    })), [count]);

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {particles.map((particle) => (
        <Particle key={particle.id} {...particle} theme={theme} />
      ))}
    </div>
  );
}

// AI Data stream lines
export function DataStreamLines() {
  const { theme } = useTheme();
  const lines = useMemo(() => Array.from({ length: 6 }, (_, i) => ({
    id: i,
    startX: Math.random() * 100,
    width: Math.random() * 30 + 20,
    y: 10 + i * 15,
    duration: 6 + Math.random() * 4,
    delay: i * 0.8
  })), []);

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {lines.map((line) => (
        <motion.div
          key={line.id}
          className="absolute h-px"
          style={{
            left: `${line.startX}%`,
            top: `${line.y}%`,
            width: `${line.width}%`,
            background: theme === 'dark'
              ? 'linear-gradient(90deg, transparent, rgba(99, 179, 237, 0.4), transparent)'
              : 'linear-gradient(90deg, transparent, rgba(20, 184, 166, 0.3), transparent)'
          }}
          animate={{
            opacity: [0, 1, 0],
            scaleX: [0.3, 1, 0.3],
            x: ['-30%', '30%', '-30%']
          }}
          transition={{
            duration: line.duration,
            delay: line.delay,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      ))}
    </div>
  );
}

// Pulsing orb effect
export function PulsingOrb({ x, y, size, color = 'primary' }) {
  const { theme } = useTheme();
  const colors = {
    primary: theme === 'dark' ? 'rgba(99, 179, 237, 0.25)' : 'rgba(20, 184, 166, 0.2)',
    secondary: theme === 'dark' ? 'rgba(251, 191, 36, 0.15)' : 'rgba(245, 158, 11, 0.15)'
  };

  return (
    <motion.div
      className="absolute rounded-full blur-3xl pointer-events-none"
      style={{
        left: x,
        top: y,
        width: size,
        height: size,
        background: colors[color],
        transform: 'translate(-50%, -50%)'
      }}
      animate={{
        scale: [1, 1.3, 1],
        opacity: [0.3, 0.6, 0.3]
      }}
      transition={{
        duration: 8,
        repeat: Infinity,
        ease: "easeInOut"
      }}
    />
  );
}

// Dot grid pattern (replacing hexagons)
export function DotGrid() {
  const { theme } = useTheme();
  
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-40">
      <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <pattern id="dotgrid" width="30" height="30" patternUnits="userSpaceOnUse">
            <circle
              cx="15"
              cy="15"
              r="1.5"
              fill={theme === 'dark' ? 'rgba(99, 179, 237, 0.4)' : 'rgba(20, 184, 166, 0.3)'}
            />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#dotgrid)" />
      </svg>
    </div>
  );
}

// Subtle grid lines pattern
export function GridLines() {
  const { theme } = useTheme();
  
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-20">
      <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <pattern id="gridlines" width="50" height="50" patternUnits="userSpaceOnUse">
            <path 
              d="M 50 0 L 0 0 0 50" 
              fill="none" 
              stroke={theme === 'dark' ? 'rgba(99, 179, 237, 0.3)' : 'rgba(20, 184, 166, 0.2)'}
              strokeWidth="0.5"
            />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#gridlines)" />
      </svg>
    </div>
  );
}

// Neural network canvas
export function NeuralNetwork({ nodeCount = 40 }) {
  const canvasRef = useRef(null);
  const { theme } = useTheme();
  const animationRef = useRef(null);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const nodes = [];
    
    const resizeCanvas = () => {
      canvas.width = canvas.offsetWidth * window.devicePixelRatio;
      canvas.height = canvas.offsetHeight * window.devicePixelRatio;
      ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
    };
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    
    // Initialize nodes
    for (let i = 0; i < nodeCount; i++) {
      nodes.push({
        x: Math.random() * canvas.offsetWidth,
        y: Math.random() * canvas.offsetHeight,
        vx: (Math.random() - 0.5) * 0.5,
        vy: (Math.random() - 0.5) * 0.5,
        radius: Math.random() * 3 + 1,
        pulsePhase: Math.random() * Math.PI * 2
      });
    }
    
    let time = 0;
    const animate = () => {
      ctx.clearRect(0, 0, canvas.offsetWidth, canvas.offsetHeight);
      time += 0.015;
      
      const primaryColor = theme === 'dark' 
        ? 'rgba(99, 179, 237, 0.7)' 
        : 'rgba(20, 184, 166, 0.6)';
      const lineColor = theme === 'dark'
        ? 'rgba(99, 179, 237, 0.15)'
        : 'rgba(20, 184, 166, 0.12)';
      
      // Update and draw nodes
      nodes.forEach((node, i) => {
        node.x += node.vx;
        node.y += node.vy;
        
        if (node.x < 0 || node.x > canvas.offsetWidth) node.vx *= -1;
        if (node.y < 0 || node.y > canvas.offsetHeight) node.vy *= -1;
        
        // Pulsing effect
        const pulse = Math.sin(time + node.pulsePhase) * 0.5 + 1;
        
        // Draw node with glow
        ctx.beginPath();
        ctx.arc(node.x, node.y, node.radius * pulse, 0, Math.PI * 2);
        ctx.fillStyle = primaryColor;
        ctx.shadowBlur = 10;
        ctx.shadowColor = primaryColor;
        ctx.fill();
        ctx.shadowBlur = 0;
        
        // Draw connections
        nodes.forEach((otherNode, j) => {
          if (i >= j) return;
          const dx = otherNode.x - node.x;
          const dy = otherNode.y - node.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          
          if (distance < 120) {
            ctx.beginPath();
            ctx.moveTo(node.x, node.y);
            ctx.lineTo(otherNode.x, otherNode.y);
            ctx.strokeStyle = lineColor;
            ctx.lineWidth = (1 - distance / 120) * 2;
            ctx.stroke();
          }
        });
      });
      
      animationRef.current = requestAnimationFrame(animate);
    };
    
    animate();
    
    return () => {
      cancelAnimationFrame(animationRef.current);
      window.removeEventListener('resize', resizeCanvas);
    };
  }, [theme, nodeCount]);
  
  return (
    <canvas 
      ref={canvasRef} 
      className="absolute inset-0 w-full h-full pointer-events-none"
      style={{ opacity: 0.5 }}
    />
  );
}

// Main animated background component with variants
export default function AnimatedBackground({ variant = 'full' }) {
  const prefersReducedMotion = typeof window !== 'undefined' 
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (prefersReducedMotion) {
    return <div className="absolute inset-0 gradient-mesh" />;
  }

  return (
    <div className="absolute inset-0 overflow-hidden">
      <div className="absolute inset-0 gradient-mesh" />
      
      {variant === 'full' && (
        <>
          {/* Original primary/secondary colors */}
          <PulsingOrb x="10%" y="20%" size={400} color="primary" />
          <PulsingOrb x="85%" y="15%" size={300} color="secondary" />
          <PulsingOrb x="70%" y="75%" size={350} color="primary" />
          <DotGrid />
          <FloatingParticles count={40} />
        </>
      )}
      
      {variant === 'neural' && (
        <>
          <NeuralNetwork nodeCount={35} />
          <FloatingParticles count={15} />
        </>
      )}
      
      {variant === 'minimal' && (
        <>
          <PulsingOrb x="20%" y="30%" size={300} color="primary" />
          <PulsingOrb x="80%" y="70%" size={250} color="primary" />
          <FloatingParticles count={20} />
        </>
      )}
      
      {variant === 'grid' && (
        <>
          <GridLines />
          <FloatingParticles count={15} />
        </>
      )}
      
      {variant === 'subtle' && (
        <>
          <PulsingOrb x="15%" y="25%" size={350} color="primary" />
          <PulsingOrb x="85%" y="75%" size={300} color="primary" />
          <FloatingParticles count={25} />
        </>
      )}
    </div>
  );
}
